/**
 * QualityScorecard — Shows after test run with supervisor validation results
 *
 * Displays: overall pass/partial/fail, per-criterion results with evidence,
 * supervisor metrics (validations, retries, tokens), and step-by-step trace.
 */

import {
  CheckCircle, XCircle, AlertTriangle, Brain, Clock,
  Zap, ChevronDown, X, BarChart3, History as HistoryIcon,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Collapsible, CollapsibleContent, CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';
import type { SuccessCriterion } from '@/lib/mock-automate-data';

export interface StepResult {
  nodeId: string;
  nodeName: string;
  nodeType: string;
  status: 'success' | 'error' | 'skipped';
  durationMs: number;
  /**
   * Supervisor verdict. `not_evaluated` means the workflow has no
   * quality criteria so there was nothing to validate against — we
   * emit this rather than "pass" to avoid a fake-success signal.
   */
  supervisorResult?: 'pass' | 'partial' | 'fail' | 'not_evaluated';
  supervisorFeedback?: string;
  retryCount?: number;
}

export interface CriterionResult {
  criterionId: string;
  description: string;
  result: 'pass' | 'partial' | 'fail';
  evidence: string;
  weight: string;
}

export interface ScorecardData {
  overall: 'pass' | 'partial' | 'fail';
  criterionResults: CriterionResult[];
  stepResults: StepResult[];
  totalDurationMs: number;
  supervisorMetrics: {
    totalValidations: number;
    totalRetries: number;
    supervisorTokens: number;
  };
}

interface QualityScorecardProps {
  data: ScorecardData;
  onClose: () => void;
  /**
   * Optional execution id for the just-finished run. When provided,
   * the scorecard surfaces an "Open in History" affordance that
   * deep-links to `/automate/history/:executionId` — the canonical
   * post-run drill-down surface.
   *
   * #197 / #205 — operators reported "no result anywhere" because the
   * scorecard appeared, scrolled past, and was dismissed without a
   * clear hand-off into History. Linking directly closes that gap.
   */
  executionId?: string | null;
  /**
   * Optional click handler for the History link. The page that owns
   * the scorecard wires this to its router; we keep navigation out
   * of this shared component to avoid pulling a router dep.
   */
  onOpenHistory?: (executionId: string) => void;
}

function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  return `${Math.floor(s / 60)}m ${s % 60}s`;
}

const RESULT_STYLES = {
  pass: { icon: CheckCircle, color: 'text-emerald-600 dark:text-emerald-400', bg: 'bg-emerald-500/10 border-emerald-500/20', label: 'PASS' },
  partial: { icon: AlertTriangle, color: 'text-amber-600 dark:text-amber-400', bg: 'bg-amber-500/10 border-amber-500/20', label: 'PARTIAL' },
  fail: { icon: XCircle, color: 'text-red-600 dark:text-red-400', bg: 'bg-red-500/10 border-red-500/20', label: 'FAIL' },
};

export function QualityScorecard({
  data,
  onClose,
  executionId,
  onOpenHistory,
}: QualityScorecardProps) {
  const passCount = data.criterionResults.filter(c => c.result === 'pass').length;
  const totalCriteria = data.criterionResults.length;
  // When no success criteria are defined we cannot evaluate quality — show a
  // neutral "no criteria" header instead of "PASS — 0/0 100%" (#103).
  const noCriteria = totalCriteria === 0;
  const overallStyle = noCriteria
    ? { icon: AlertTriangle, color: 'text-muted-foreground', bg: 'bg-muted/30 border-border', label: 'NOT EVALUATED' }
    : RESULT_STYLES[data.overall];
  const OverallIcon = overallStyle.icon;
  const passRate = noCriteria ? null : Math.round((passCount / totalCriteria) * 100);

  return (
    <div className="w-[380px] border-l bg-card flex flex-col shrink-0">
      {/* Header */}
      <div className={cn('px-4 py-3 border-b flex items-center gap-3', overallStyle.bg, 'border')}>
        <OverallIcon className={cn('h-6 w-6', overallStyle.color)} />
        <div className="flex-1">
          <h3 className="text-sm font-semibold">Quality Scorecard</h3>
          <p className={cn('text-xs font-medium', overallStyle.color)}>
            {noCriteria
              ? 'No success criteria defined — cannot evaluate'
              : `${overallStyle.label} — ${passCount}/${totalCriteria} criteria met`}
          </p>
        </div>
        {/* #197 / #205 — direct deep-link to /automate/history/:executionId
            so operators can immediately see the persisted run row + full
            step trace without manually navigating + searching the list. */}
        {executionId && onOpenHistory && (
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={() => onOpenHistory(executionId)}
            title="Open this run in History"
            aria-label="Open this run in History"
          >
            <HistoryIcon className="h-3.5 w-3.5" />
          </Button>
        )}
        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onClose} title="Close scorecard">
          <X className="h-3.5 w-3.5" />
        </Button>
      </div>

      <ScrollArea className="flex-1">
        <div className="p-4 space-y-4">

          {/* Overall metrics */}
          <div className="grid grid-cols-3 gap-2">
            <div className="bg-muted/30 rounded-md p-2 text-center">
              {/* Show "—" when there are no criteria so we never report a
                  fabricated 100% quality score (#103). */}
              <p className="text-lg font-bold">{passRate === null ? '—' : `${passRate}%`}</p>
              <p className="text-[9px] text-muted-foreground">Quality Score</p>
            </div>
            <div className="bg-muted/30 rounded-md p-2 text-center">
              <p className="text-lg font-bold">{formatDuration(data.totalDurationMs)}</p>
              <p className="text-[9px] text-muted-foreground">Total Duration</p>
            </div>
            <div className="bg-muted/30 rounded-md p-2 text-center">
              <p className="text-lg font-bold">{data.stepResults.length}</p>
              <p className="text-[9px] text-muted-foreground">Steps Run</p>
            </div>
          </div>

          {/* Supervisor overhead */}
          <div className="bg-muted/20 rounded-md p-2.5 flex items-center gap-3 text-[10px] text-muted-foreground">
            <Brain className="h-4 w-4 text-primary shrink-0" />
            <span>{data.supervisorMetrics.totalValidations} validations</span>
            <span>·</span>
            <span>{data.supervisorMetrics.totalRetries} retries</span>
            <span>·</span>
            <span>{data.supervisorMetrics.supervisorTokens.toLocaleString()} tokens</span>
          </div>

          <Separator />

          {/* Per-criterion results */}
          <div className="space-y-2">
            <h4 className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Quality Criteria</h4>
            {noCriteria && (
              <div className="rounded-md border border-dashed p-2.5 text-[10px] text-muted-foreground">
                This workflow has no success criteria. Define criteria in the
                workflow's Purpose &amp; Intelligence settings before running a
                quality-graded test.
              </div>
            )}
            {data.criterionResults.map(cr => {
              const style = RESULT_STYLES[cr.result];
              const CrIcon = style.icon;
              return (
                <div key={cr.criterionId} className={cn('rounded-md border p-2.5', style.bg)}>
                  <div className="flex items-start gap-2">
                    <CrIcon className={cn('h-3.5 w-3.5 shrink-0 mt-0.5', style.color)} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className="text-xs font-medium">{style.label}</span>
                        <Badge variant="outline" className={cn('text-[7px] h-3 px-1', cr.weight === 'critical' ? 'text-red-500' : cr.weight === 'important' ? 'text-amber-500' : '')}>{cr.weight}</Badge>
                      </div>
                      <p className="text-[10px] text-muted-foreground mt-0.5">{cr.description}</p>
                      <p className="text-[9px] mt-1 italic">{cr.evidence}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <Separator />

          {/* Step-by-step trace */}
          <Collapsible defaultOpen>
            <CollapsibleTrigger className="flex items-center gap-1.5 w-full text-left py-1">
              <ChevronDown className="h-3 w-3 text-muted-foreground" />
              <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Step Trace ({data.stepResults.length} steps)</span>
            </CollapsibleTrigger>
            <CollapsibleContent className="mt-1.5 space-y-1">
              {data.stepResults.map((step, i) => (
                <div key={step.nodeId} className="flex items-center gap-2 py-1.5 px-2 rounded-md bg-muted/20">
                  <span className="text-[9px] text-muted-foreground w-4 shrink-0">{i + 1}</span>
                  {step.status === 'success' ? (
                    <CheckCircle className="h-3 w-3 text-emerald-500 shrink-0" />
                  ) : step.status === 'error' ? (
                    <XCircle className="h-3 w-3 text-red-500 shrink-0" />
                  ) : (
                    <AlertTriangle className="h-3 w-3 text-zinc-400 shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <span className="text-[10px] font-medium truncate block">{step.nodeName}</span>
                    {step.supervisorFeedback && (
                      <p className="text-[8px] text-muted-foreground italic truncate">{step.supervisorFeedback}</p>
                    )}
                  </div>
                  <span className="text-[9px] text-muted-foreground font-mono shrink-0">{formatDuration(step.durationMs)}</span>
                  {step.retryCount && step.retryCount > 0 && (
                    <Badge variant="outline" className="text-[7px] h-3 px-1 text-amber-500">retry:{step.retryCount}</Badge>
                  )}
                </div>
              ))}
            </CollapsibleContent>
          </Collapsible>
        </div>
      </ScrollArea>
    </div>
  );
}
